import { useState, useRef, useCallback, useEffect, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import PageContainer from '../../shared/pageContainer';
import SurveyReportInfo from '../../shared/surveyReportInfo';
import AttributeReviewReport from '../../shared/attributeReviewReport';
import QuestionnaireReviewReport from '../../shared/questionnaireReviewReport';
import ReportDescription from '../../shared/reportDescription';
import NotificationCard from '../../shared/notificationCard';
import TextBoxWithTooltip from '../../shared/textBoxWithTooltip';
import SurveyIconInfoTooltip from '../../shared/surveyIconInfoTooltip';
import ActionButton from '../../shared/actionButton';
import SurveySummaryDrawerForm from '../../shared/surveySummaryDrawerForm';
import ReviewCommentsDrawer from '../../shared/reviewCommentsDrawer';
import NpsReviewReport from '../../shared/npsReviewReport';
import {
  api_review_score_per_reviewer,
  api_review_score_per_subject,
  api_survey_summary_report,
  api_text_reply_review_results,
} from '../../../constants/apiRoutes';
import {
  SURVEY_TYPES,
  CREATE_SURVEY_FIELD_NAMES,
} from '../../../constants/survey';
import { SURVEY_STATUS_TYPES } from '../../../constants/statuses';
import { APP_PAGES } from '../../../constants/pages';
import { PERMISSION_CODES } from '../../../constants/permissions';
import { sticky } from '../../../constants/helperCssRules';
import { surveySelector } from '../../../store/selectors/surveySelector';
import { peopleSelector } from '../../../store/selectors/peopleSelector';
import {
  getSurvey,
  getReviews,
  getReview,
  getQuestionnaires,
  getQuestionnaireReport,
  clearSurvey,
  clearReviews,
  clearQuestionnaires,
} from '../../../store/modules/survey';
import { getAllUsers } from '../../../store/modules/people';
import { useTranslations } from '../../../utility/useTranslations';
import {
  getItemById,
  isArrayEmpty,
  isObjectEmpty,
} from '../../../utility/helpers';
import http from '../../../utility/http';
import {
  hasAuthPermissionsCheck,
  isUserProfileAccessibleCheck,
} from '../../../utility/permissions';
import {
  EVENT_ACTION_TYPES,
  tagManagerDataLayer,
} from '../../../utility/tagManager';
import { prepareCycleReviewTypes } from '../../../utility/cycles';
import {
  prepareSurveyTypes,
  prepareSurveyStatuses,
  hasReviewCommentsCheck,
  prepareReviewComments,
  prepareReviewCommentsBySelectedView,
  prepareCommentsByQuestion,
} from '../../../utility/survey';
import { usePreviousValue } from '../../../utility/hooks';
import { showSuccessMessage } from '../../../utility/uiUtils';
import { ACTION_BUTTON_TYPES } from '../../shared/actionButton/config';
import { hasResultsCheck } from './config';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  title: {
    maxWidth: 'calc(100% - 260px)',
    width: '100%',
  },
  titleWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  titleContent: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: 'calc(100% - 38px)',
  },
  anonymous: {
    display: 'block',
  },
  infoIcon: {
    flexShrink: 0,
    '& svg': {
      marginRight: 0,
    },
  },
  sharedIcon: {
    '& svg': {
      width: 18,
      height: 18,
    },
  },
  divider: {
    paddingTop: spacing(8),
    ...sticky(primary.white, 105),
  },
  description: {
    marginTop: spacing(6),
    marginBottom: spacing(5),
  },
  reportButton: {
    marginLeft: spacing(2),
    width: 30,
    height: 30,
    '& svg': {
      width: 16,
    },
  },
  review: {
    marginBottom: spacing(4),
    '&:last-of-type': {
      marginBottom: 0,
    },
  },
}));

const SurveyReportPage = ({
  navigate,
  dispatch,
  organizationSettings,
  auth,
  accessControl,
  ...rest
}) => {
  const classes = useStyles();
  const translations = useTranslations(APP_PAGES.SURVEY_REPORT);
  const reviewRef = useRef({});
  const pageScrollRef = useRef(null);

  const { surveyId } = useParams();
  const { survey, reviews, questionnaires } = useSelector(surveySelector);
  const { allUsers } = useSelector(peopleSelector);
  const [isLoading, setIsLoading] = useState(true);
  const [reviewId, setReviewId] = useState(null);
  const [textReplyQuestionId, setTextReplyQuestionId] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState({});
  const [isSummaryDrawerOpened, setIsSummaryDrawerOpened] = useState(false);
  const [isReviewCommentsDrawerOpened, setIsReviewCommentsDrawerOpened] =
    useState(false);
  const [commentators, setCommentators] = useState([]);
  const [selectedCommentator, setSelectedCommentator] = useState({});
  const [selectedQuestionCommentsId, setSelectedQuestionCommentsId] =
    useState(null);
  const [reviewComments, setReviewComments] = useState([]);
  const [isViewAllComments, setIsViewAllComments] = useState(false);

  const [canManageAllSurveys, canManageSurveysCreated] =
    hasAuthPermissionsCheck(
      [
        PERMISSION_CODES.SURVEYS_MANAGE_ALL,
        PERMISSION_CODES.SURVEYS_MANAGE_CREATED,
      ],
      accessControl
    );

  const previousReviewId = usePreviousValue(reviewId);

  const { title, isAnonymous, isOneTime, type, createdFor, reviewers, cycle } =
    survey;
  const isNpsReview = [SURVEY_TYPES.ENPS, SURVEY_TYPES.NPS].includes(type);
  const isEnpsReview = type === SURVEY_TYPES.ENPS;
  const isTeamReview = type === SURVEY_TYPES.TEAM;
  const isPartOfCycle = !!cycle;
  const statuses = prepareSurveyStatuses(translations.statuses);
  const types = isPartOfCycle
    ? prepareCycleReviewTypes(translations.cycleReviewTypes)
    : prepareSurveyTypes(translations);
  const hasResults = useMemo(
    () => hasResultsCheck(reviews, questionnaires),
    [reviews, questionnaires]
  );
  const hasQuestionnaire =
    !isArrayEmpty(questionnaires) || !!survey.questionnaire?.id;
  const shouldHideReport =
    (isArrayEmpty(createdFor) || isArrayEmpty(reviewers)) && !hasQuestionnaire;
  const defaultPageOffset = 137; // Header height + divider height
  const defaultReviewOffset = 48; // Height of collapsed review
  const reviewCommentsByQuestion = useMemo(() => {
    return prepareCommentsByQuestion(reviewComments, selectedUsers[reviewId]);
  }, [reviewId, reviewComments, selectedUsers]);
  const reviewCommentsBySelectedView = useMemo(
    () =>
      prepareReviewCommentsBySelectedView(
        reviewComments,
        reviewCommentsByQuestion,
        selectedCommentator,
        selectedQuestionCommentsId,
        isViewAllComments,
        isTeamReview
      ),
    [
      reviewComments,
      selectedCommentator,
      reviewCommentsByQuestion,
      selectedQuestionCommentsId,
      isViewAllComments,
      isTeamReview,
    ]
  );

  const goToProfilePage = userId => navigate(`/people/${userId}`);

  const canAccessUserProfileCheck = user =>
    isUserProfileAccessibleCheck(accessControl, auth.id, user.id);

  const hadleReviewScroll = useCallback(() => {
    if (previousReviewId !== reviewId) {
      const prevReview =
        reviewRef?.current?.[previousReviewId]?.getBoundingClientRect();
      const prevReviewOffset =
        reviewRef?.current?.[previousReviewId]?.offsetTop;
      const currReviewOffset = reviewRef?.current?.[reviewId]?.offsetTop;

      if (prevReview && prevReviewOffset < currReviewOffset) {
        setTimeout(() => {
          pageScrollRef?.current?.scrollTo({
            top:
              currReviewOffset -
              prevReview.height -
              defaultPageOffset +
              defaultReviewOffset,
            behavior: 'smooth',
          });
        }, 300);
      } else {
        setTimeout(() => {
          pageScrollRef?.current?.scrollTo({
            top: currReviewOffset - defaultPageOffset,
            behavior: 'smooth',
          });
        }, 300);
      }
    }
  }, [previousReviewId, reviewId]);

  const onGetAllReviewComments = useCallback(
    async (surveyRecord, currentReview) => {
      if (!currentReview.isEnps) {
        const { data } = await http.get(
          api_text_reply_review_results(surveyId, currentReview.id)
        );
        const { reviewCommentators, allComments } = prepareReviewComments(
          translations.review.neutral.notApplicable,
          data,
          surveyRecord,
          currentReview
        );
        setCommentators(reviewCommentators);
        setReviewComments(allComments);
      }
    },
    [translations, surveyId]
  );

  const handleViewReport = useCallback(
    (attributeId, surveyData = {}) =>
      async () => {
        if (attributeId !== reviewId) {
          if (
            reviewRef?.current?.[reviewId] &&
            reviewRef?.current?.[reviewId]?.offsetTop <
              reviewRef?.current?.[attributeId]?.offsetTop
          ) {
            pageScrollRef?.current?.scrollTo({
              top: 0,
            });
          }
          const surveyRecord = isObjectEmpty(surveyData) ? survey : surveyData;
          const review = await getReview(dispatch, surveyId, attributeId);
          const surveyAttributes = surveyRecord.attributes;
          const hasAnswerComment = hasReviewCommentsCheck(
            attributeId,
            surveyAttributes
          );

          if (hasAnswerComment) {
            onGetAllReviewComments(surveyRecord, review);
          }
          setReviewId(attributeId);
          setTextReplyQuestionId(null);
        } else {
          setReviewId(null);
          setReviewComments([]);
          setCommentators([]);
        }
      },
    [surveyId, survey, reviewId, dispatch, onGetAllReviewComments]
  );

  const getSurveyData = async () => {
    const { VisitSDP } = EVENT_ACTION_TYPES;

    const [surveyData, reviewsData] = await Promise.all([
      getSurvey(dispatch, surveyId),
      getReviews(dispatch, surveyId),
      getAllUsers(dispatch),
    ]);

    const isDraft =
      surveyData[CREATE_SURVEY_FIELD_NAMES.STATUS] ===
      SURVEY_STATUS_TYPES.DRAFT;

    if (isDraft) {
      return navigate(`/not-found`, { replace: true });
    }

    if (surveyData.questionnaire?.id) {
      const questionnairesData = await getQuestionnaires(dispatch, surveyId);
      await Promise.all(
        questionnairesData.map(questionnaire =>
          getQuestionnaireReport(dispatch, surveyId, questionnaire.id)
        )
      );
    }

    if (!isArrayEmpty(reviewsData)) {
      handleViewReport(reviewsData[0].id, surveyData)();
    }

    setIsLoading(false);
    tagManagerDataLayer(VisitSDP.action, VisitSDP.name);
  };

  const handleViewTextReply = questionId => e => {
    e.stopPropagation();

    setTextReplyQuestionId(prevTextReplyQuestionId =>
      prevTextReplyQuestionId !== questionId ? questionId : null
    );
  };

  const handleViewIndividualScores = attributeId => () =>
    survey.type === SURVEY_TYPES.TEAM
      ? http.get(api_review_score_per_subject(survey.id, attributeId))
      : http.get(api_review_score_per_reviewer(survey.id, attributeId));

  const handleViewAllComents = e => {
    e.stopPropagation();
    setIsReviewCommentsDrawerOpened(true);
    setIsViewAllComments(true);
  };

  const handleViewReviewerComments = userId => {
    setSelectedCommentator(getItemById(commentators, userId));
    setIsReviewCommentsDrawerOpened(true);
  };

  const handleViewQuestionComments = questionId => {
    setSelectedQuestionCommentsId(questionId);
    setIsReviewCommentsDrawerOpened(true);
  };

  const handleCloseReviewCommentsDrawer = e => {
    e.stopPropagation();
    setIsReviewCommentsDrawerOpened(false);
    setSelectedCommentator({});
    setIsViewAllComments(false);
    setSelectedQuestionCommentsId(null);
  };

  const onUserSelect = useCallback(
    currentReviewId => (selectedUserId, isSelected) => {
      if (selectedUsers[currentReviewId]) {
        if (isSelected) {
          return setSelectedUsers(prevSelectedUsers => ({
            ...prevSelectedUsers,
            [currentReviewId]: prevSelectedUsers[currentReviewId].filter(
              userId => userId !== selectedUserId
            ),
          }));
        }

        return setSelectedUsers(prevSelectedUsers => ({
          ...prevSelectedUsers,
          [currentReviewId]: [
            ...prevSelectedUsers[currentReviewId],
            selectedUserId,
          ],
        }));
      }

      setSelectedUsers(prevSelectedUsers => ({
        ...prevSelectedUsers,
        [currentReviewId]: [selectedUserId],
      }));
    },
    [selectedUsers]
  );

  const onClearSelectedUsers = useCallback(
    currentId => () => {
      setSelectedUsers(prevSelectedUsers => {
        return Object.keys(prevSelectedUsers).reduce(
          (acc, selectedKey) => ({
            ...acc,
            ...(+selectedKey !== currentId
              ? { [selectedKey]: [...prevSelectedUsers[selectedKey]] }
              : {}),
          }),
          {}
        );
      });
    },
    []
  );

  useEffect(() => {
    getSurveyData();

    return () => {
      dispatch(clearSurvey());
      dispatch(clearReviews());
      dispatch(clearQuestionnaires());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onOpenSummaryDrawer = () => setIsSummaryDrawerOpened(true);

  const onCloseSummaryDrawer = () => setIsSummaryDrawerOpened(false);

  const onSaveSummary = async values => {
    const summaryData = {
      title: values.title,
      ...(values.description ? { description: values.description } : {}),
      report_data: values.report_data,
      share_to: values.share_to,
      anon_reviewers: values.anon_reviewers,
    };

    await http.post(api_survey_summary_report(surveyId), summaryData);
    showSuccessMessage(translations.surveySummaryForm.successMessage);
  };

  const renderReportHeader = () => {
    return (
      <div className={classes.title}>
        {!isLoading && (
          <div className={classes.titleWrapper}>
            <div className={classes.titleContent}>
              <TextBoxWithTooltip variant="h1" text={title} />
              {isOneTime && (
                <SurveyIconInfoTooltip
                  className={classes.infoIcon}
                  tooltipText={translations.oneTimeSurvey}
                  isOneTimeSurvey
                />
              )}
              {isAnonymous && (
                <SurveyIconInfoTooltip
                  className={classNames(classes.anonymous, classes.infoIcon)}
                  tooltipText={translations.anonymousSurvey}
                />
              )}
            </div>
            {(canManageAllSurveys || canManageSurveysCreated) && (
              <div className={classes.reportButton}>
                <ActionButton
                  type={ACTION_BUTTON_TYPES.REPORT_CREATE}
                  tooltipText={translations.createSummaryReport}
                  disabledTooltipText={
                    isPartOfCycle
                      ? translations.createCycleSummaryReportDisabled
                      : translations.createSummaryReportDisabled
                  }
                  isDisabled={
                    survey[CREATE_SURVEY_FIELD_NAMES.STATUS] !==
                      SURVEY_STATUS_TYPES.COMPLETED || !hasResults
                  }
                  onClickHandler={onOpenSummaryDrawer}
                />
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    !isLoading && (
      <PageContainer
        {...rest}
        translations={translations}
        headerProps={{
          renderTitle: renderReportHeader,
        }}
        auth={auth}
        accessControl={accessControl}
        organizationSettings={organizationSettings}
        navigate={navigate}
        pageScrollRef={pageScrollRef}
        shouldPassProps={false}
        isFullWidthContent
      >
        <div>
          <div className={classes.divider} />
          {!shouldHideReport && (
            <>
              <SurveyReportInfo
                labels={translations.info}
                survey={survey}
                statuses={statuses}
                types={types}
                isPartOfCycle={isPartOfCycle}
                isTeamReview={isTeamReview}
                canAccessUserProfileCheck={canAccessUserProfileCheck}
                onGoToPersonProfile={goToProfilePage}
              />
              {!isNpsReview && (
                <ReportDescription
                  className={classes.description}
                  translations={translations.surveyTypesDescription}
                  createdFor={createdFor}
                  reviewers={reviewers}
                  surveyType={type}
                  canAccessUserProfileCheck={canAccessUserProfileCheck}
                  onGoToPersonProfile={goToProfilePage}
                  canGoToReviewerProfile
                />
              )}
              {reviews.map(review => (
                <div
                  key={`review_${review.id}`}
                  ref={el => {
                    reviewRef.current[review.id] = el;
                  }}
                  className={classes.review}
                >
                  {isNpsReview ? (
                    <NpsReviewReport
                      translations={translations.review}
                      surveyType={survey.type}
                      review={review}
                      reviewersCount={survey?.reviewers?.length}
                      isCompleted={
                        survey?.status === SURVEY_STATUS_TYPES.COMPLETED
                      }
                      shouldDisableIndividualScores={
                        isEnpsReview || isAnonymous
                      }
                      onViewIndividualScores={handleViewIndividualScores(
                        review.id
                      )}
                      hasCompletedCount
                    />
                  ) : (
                    <AttributeReviewReport
                      translations={translations.review}
                      review={review}
                      reviewComments={reviewCommentsByQuestion}
                      selectedUsers={selectedUsers[review.id]}
                      reviewersCount={survey?.reviewers?.length}
                      commentators={commentators}
                      isExpanded={reviewId === review.id}
                      isCompleted={
                        survey?.status === SURVEY_STATUS_TYPES.COMPLETED
                      }
                      hasComments={hasReviewCommentsCheck(
                        review.id,
                        survey.attributes
                      )}
                      isTeamReview={isTeamReview}
                      hasViewAllComments={isAnonymous}
                      isReadOnlyFeedback={isAnonymous}
                      textReplyQuestionId={textReplyQuestionId}
                      shouldDisableIndividualScores={isAnonymous}
                      canAccessUserProfileCheck={canAccessUserProfileCheck}
                      onUserSelect={onUserSelect(review.id)}
                      onClearSelectedUsers={onClearSelectedUsers(review.id)}
                      onViewIndividualScores={handleViewIndividualScores(
                        review.id
                      )}
                      onGoToPersonProfile={goToProfilePage}
                      onViewTextReply={handleViewTextReply}
                      onViewReport={handleViewReport(review.id)}
                      onScrollInToView={hadleReviewScroll}
                      onViewReviewerComments={handleViewReviewerComments}
                      onViewAllComments={handleViewAllComents}
                      onViewQuestionComments={handleViewQuestionComments}
                      hasCompletedCount
                    />
                  )}
                </div>
              ))}
              {questionnaires.map(questionnaire => (
                <div
                  key={`questionnaire_${questionnaire.id}`}
                  className={classes.review}
                >
                  <QuestionnaireReviewReport
                    translations={translations.review}
                    questionnaire={questionnaire}
                    selectedUsers={selectedUsers[questionnaire.id]}
                    reviewersCount={survey?.reviewers?.length}
                    isTeamReview={isTeamReview}
                    isCompleted={
                      survey?.status === SURVEY_STATUS_TYPES.COMPLETED
                    }
                    textReplyQuestionId={textReplyQuestionId}
                    hasCompletedCount
                    onUserSelect={onUserSelect(questionnaire.id)}
                    onClearSelectedUsers={onClearSelectedUsers(
                      questionnaire.id
                    )}
                    onViewTextReply={handleViewTextReply}
                  />
                </div>
              ))}
            </>
          )}
          <ReviewCommentsDrawer
            translations={translations.reviewComments}
            isOpened={isReviewCommentsDrawerOpened}
            comments={reviewCommentsBySelectedView}
            commentator={selectedCommentator}
            onClose={handleCloseReviewCommentsDrawer}
            isViewAll={isViewAllComments}
            hasCancelButton
          />
          <NotificationCard
            title={translations.noReport}
            shouldFade={shouldHideReport}
          />
          <SurveySummaryDrawerForm
            translations={translations.surveySummaryForm}
            isOpened={isSummaryDrawerOpened}
            survey={survey}
            reviews={reviews}
            questionnaires={questionnaires}
            allUsers={allUsers}
            selectedUsers={selectedUsers}
            onClose={onCloseSummaryDrawer}
            onSave={onSaveSummary}
          />
        </div>
      </PageContainer>
    )
  );
};

SurveyReportPage.propTypes = {
  organizationSettings: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired,
  accessControl: PropTypes.object.isRequired,
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
};

export default memo(SurveyReportPage);
